import React, { useState, useRef } from "react";
import axios from "axios";
import Popup from "reactjs-popup";
import { toastSuccess, toastError } from "../../../shared/toastHelper";
import EditIcon from "../../shared/Icons/EditIcon";
import TrashIcon from "../../shared/Icons/TrashIcon";
import XCloseIcon from "../../shared/Icons/XCloseIcon";
import ChevronRight from "../../shared/Icons/ChevronRight";
import deleteModalContentStyle from "../../../shared/deleteModalContentStyle";
import V3OfferTemplateEditDrawer from "../../OfferEditUI/components/V3OfferTemplateEditDrawer";
import "../styles/offerFileNameBuilder.css";

const OfferTemplatesManager = ({ teamId, csrfToken, offerV3, initialTemplates }) => {
  const [templates, setTemplates] = useState(initialTemplates || []);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [search, setSearch] = useState("");
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const savingRef = useRef(false);

  const isV3 = offerV3;

  const renamePath = (id) => {
    if (isV3) {
      return `/teams/${teamId}/offer_templates/v3/${id}`;
    }
    return `/teams/${teamId}/budget_templates/${id}/rename`;
  };

  const deletePath = (id) => {
    if (isV3) {
      return `/teams/${teamId}/offer_templates/v3/${id}`;
    }
    return `/teams/${teamId}/budget_templates/${id}`;
  };

  const templateParam = isV3 ? "v3_offer_template" : "budget";

  const startEditing = (template) => {
    setEditingId(template.id);
    setEditingName(template.name);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveRename = (id) => {
    if (savingRef.current) return;
    savingRef.current = true;

    const trimmed = editingName.trim();
    if (!trimmed) {
      toastError("Name cannot be blank.");
      savingRef.current = false;
      return;
    }

    const original = templates.find(t => t.id === id);
    if (original && original.name === trimmed) {
      cancelEditing();
      savingRef.current = false;
      return;
    }

    const headers = csrfToken ? { "X-CSRF-TOKEN": csrfToken } : {};

    axios.patch(renamePath(id), {
      [templateParam]: { name: trimmed }
    }, { headers })
    .then(() => {
      setTemplates(prev => prev.map(t => t.id === id ? { ...t, name: trimmed } : t));
      cancelEditing();
      toastSuccess("Template renamed.");
    })
    .catch(() => {
      toastError("Failed to rename template.");
    })
    .finally(() => {
      savingRef.current = false;
    });
  };

  const deleteTemplate = (id) => {
    const headers = csrfToken ? { "X-CSRF-TOKEN": csrfToken } : {};

    axios.delete(deletePath(id), { headers })
    .then(() => {
      setTemplates(prev => prev.filter(t => t.id !== id));
      toastSuccess("Template deleted.");
    })
    .catch(() => {
      toastError("Failed to delete template.");
    });
  };

  const handleKeyDown = (e, id) => {
    if (e.key === "Enter") {
      e.preventDefault();
      saveRename(id);
    } else if (e.key === "Escape") {
      cancelEditing();
    }
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ cursor: "pointer" }}
        onClick={() => setExpanded(!expanded)}
      >
        <div>
          <p className="mb-1 offer-settings-card__title">
            Offer Templates
            {templates.length > 0 && (
              <span className="text-muted ml-2" style={{ fontSize: 13, fontWeight: 400 }}>
                ({templates.length})
              </span>
            )}
          </p>
          <p className="m-0 small text-muted">
            Rename or delete your offer templates. To create or edit a template, use the offer builder on an event.
          </p>
        </div>
        <span className="text-muted d-inline-flex" style={{ transform: expanded ? "rotate(-90deg)" : "rotate(90deg)" }}>
          <ChevronRight fill="currentColor" width="16" height="16" />
        </span>
      </div>

      {expanded && (
        <div style={{ marginTop: 16 }}>
          {templates.length === 0 ? (
            <p className="small text-muted m-0">
              No offer templates yet. Create one from the offer builder on an event.
            </p>
          ) : (
            <div style={{ maxWidth: 720 }}>
              {templates.length > 5 && (
                <div style={{ marginBottom: 12 }}>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search templates..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ maxWidth: 300 }}
                  />
                </div>
              )}
              {filteredTemplates.length === 0 ? (
                <p className="small text-muted m-0">No templates match your search.</p>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {filteredTemplates.map((template) => (
                    <div
                      key={template.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid var(--sl-border-primary)",
                        borderRadius: 8,
                        padding: "10px 16px",
                        backgroundColor: "var(--sl-bg-primary)",
                      }}
                    >
                      {editingId === template.id ? (
                        <input
                          type="text"
                          autoFocus
                          value={editingName}
                          onChange={(e) => setEditingName(e.target.value)}
                          onBlur={() => saveRename(template.id)}
                          onKeyDown={(e) => handleKeyDown(e, template.id)}
                          style={{
                            flex: 1,
                            border: "none",
                            outline: "none",
                            background: "transparent",
                            fontSize: 14,
                            color: "var(--sl-text-primary)",
                            fontFamily: "'Open Sans', sans-serif",
                          }}
                        />
                      ) : (
                        <span
                          style={{
                            flex: 1,
                            fontSize: 14,
                            color: "var(--sl-text-primary)",
                            fontFamily: "'Open Sans', sans-serif",
                          }}
                        >
                          {template.name}
                        </span>
                      )}
                      <button
                        type="button"
                        aria-label="Rename template"
                        style={{ color: "var(--sl-text-placeholder)", background: "none", border: "none", padding: 0, flexShrink: 0, marginRight: 12, display: "inline-flex", cursor: "pointer" }}
                        onClick={(e) => {
                          e.preventDefault();
                          startEditing(template);
                        }}
                      >
                        <EditIcon fill="currentColor" width="16" height="16" />
                      </button>
                      {isV3 && (
                        <button
                          type="button"
                          aria-label="Edit deal terms"
                          onClick={(e) => { e.preventDefault(); setEditingTemplateId(template.id); }}
                          style={{ color: "var(--sl-text-placeholder)", background: "none", border: "none", padding: 0, flexShrink: 0, marginRight: 12, display: "inline-flex", cursor: "pointer", fontSize: 13, fontWeight: 600 }}
                        >
                          Edit
                        </button>
                      )}
                      <Popup
                        trigger={
                          <button
                            type="button"
                            aria-label="Delete template"
                            style={{ color: "var(--sl-text-placeholder)", background: "none", border: "none", padding: 0, flexShrink: 0, display: "inline-flex", cursor: "pointer" }}
                            onClick={(e) => e.preventDefault()}
                          >
                            <TrashIcon fill="currentColor" width="16" height="16" />
                          </button>
                        }
                        onOpen={(e) => e.preventDefault()}
                        modal
                        contentStyle={deleteModalContentStyle}
                        closeOnDocumentClick
                      >
                        {close => (
                          <div>
                            <div className="row">
                              <div className="col">
                                <div className="row">
                                  <div className="col">
                                    <h5 style={{ marginBottom: "16px" }}>
                                      <strong>Delete template?</strong>
                                    </h5>
                                  </div>
                                  <div className="col-xs-auto">
                                    <button
                                      type="button"
                                      aria-label="Close"
                                      className="text-dark"
                                      style={{ paddingRight: "15px", background: "none", border: "none", display: "inline-flex", cursor: "pointer" }}
                                      onClick={(e) => { e.preventDefault(); close(); }}
                                    >
                                      <XCloseIcon fill="currentColor" width="16" height="16" />
                                    </button>
                                  </div>
                                </div>
                                <p>You're about to delete <strong>{template.name}</strong>.</p>
                                <p>This cannot be undone.</p>
                                <div className="text-right">
                                  <button
                                    type="button"
                                    className="team-btn-secondary"
                                    onClick={(e) => { e.preventDefault(); close(); }}
                                  >
                                    <span>Cancel</span>
                                  </button>
                                  <button
                                    type="button"
                                    className="team-btn-danger ml-3"
                                    onClick={(e) => { e.preventDefault(); close(); deleteTemplate(template.id); }}
                                  >
                                    <span>Delete</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </Popup>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isV3 && (
        <V3OfferTemplateEditDrawer
          team={{ id: teamId }}
          templateId={editingTemplateId}
          open={editingTemplateId !== null}
          onClose={() => setEditingTemplateId(null)}
          onSaved={(t) => setTemplates((prev) => prev.map((x) => (x.id === t.id ? { ...x, name: t.name } : x)))}
          csrfToken={csrfToken}
          contextLabel="Offer template"
        />
      )}
    </div>
  );
};

export default OfferTemplatesManager;
